import {observer} from "mobx-react";
import * as React from "react";
import {withAppStores} from "../../../../../AppStores";
import type {PortfolioGroup} from "../../../../../data/models/PortfolioGroup";
import type {AppState} from "../../../../../data/state/AppState";
import type {TransportLayer} from "../../../../../data/TransportLayer";
import {FieldV5} from "../../../details/FieldV5";
import {ClickToEditInputV5} from "../../../input/clicktoedit/ClickToEditInputV5";
import {FieldDataType, XyiconFeature} from "../../../../../generated/api/base";
import {DateFormatter} from "../../../../../utils/format/DateFormatter";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {FlexCenter} from "../../../styles/styles";
import InfoIcon from "../../../icons/circle-information.svg?react";
import {UserGroupDetailsContentStyled} from "../usergroups/UserGroupDetailsContentV5";
import {NodataUserDetailsContentStyled, NoDataStyled} from "../users/UserDetailsContentV5";
import {DetailsContentV5HeaderStyled} from "../../../details/DetailsContentV5HeaderStyled";
import {PortfolioGroupPortfoliosV5} from "./PortfolioGroupPortfoliosV5";

interface IPortfolioGroupDetailsContentProps {
	readonly portfolioGroups: PortfolioGroup[];
	readonly appState?: AppState;
	readonly transport?: TransportLayer;
}

@withAppStores("appState", "transport")
@observer
export class PortfolioGroupDetailsContentV5 extends React.Component<IPortfolioGroupDetailsContentProps> {
	constructor(props: IPortfolioGroupDetailsContentProps) {
		super(props);
	}

	private async updateApi() {
		const portfolioGroup = this.props.portfolioGroups[0];

		if (portfolioGroup.id) {
			// we're in edit mode
			await this.props.transport.services.feature.update(portfolioGroup.id, XyiconFeature.PortfolioGroup, {
				portfolios: portfolioGroup.portfolioIds,
				name: portfolioGroup.name,
			});
		}
	}

	private isNameValid = (name: string) => {
		const {portfolioGroups, appState} = this.props;
		const portfolioGroup = portfolioGroups[0];

		const portfolioGroupListArray = appState.actions.getList<PortfolioGroup>(XyiconFeature.PortfolioGroup);
		const otherGroupsWithSameName = portfolioGroupListArray.find((g) => g.id !== portfolioGroup.id && StringUtils.equalsIgnoreCase(g.name, name));

		return !otherGroupsWithSameName;
	};

	private getErrorMessage = (name: string) => {
		return !name.trim() ? "Name cannot be empty!" : this.isNameValid(name) ? "" : "Value needs to be unique!";
	};

	private onChangeName = (name: string) => {
		if (this.isNameValid(name)) {
			this.props.portfolioGroups[0].name = name;
			this.updateApi();
		}
	};

	private onBlur = () => {
		const portfolioGroup = this.props.portfolioGroups[0];

		if (portfolioGroup) {
			this.updateApi();
		}
	};

	private get portfolioGroupMemberCount() {
		const {appState, portfolioGroups} = this.props;

		return portfolioGroups[0].portfolioIds.filter((id) => appState.actions.getFeatureItemById(id, XyiconFeature.Portfolio)).length;
	}

	public override render() {
		const {portfolioGroups, appState} = this.props;
		const portfolioGroup = portfolioGroups[0];

		return (
			<NodataUserDetailsContentStyled>
				{portfolioGroups.length === 0 && <NoDataStyled>Please select a Portfolio Group</NoDataStyled>}
				{portfolioGroups.length === 1 && (
					<>
						<UserGroupDetailsContentStyled>
							<DetailsContentV5HeaderStyled>
								<FlexCenter $gap="10px">
									<InfoIcon />
									<h2>Details</h2>
								</FlexCenter>
							</DetailsContentV5HeaderStyled>
							<div className="header">
								<FieldV5 label="Name">
									<ClickToEditInputV5
										value={portfolioGroup.name}
										onChange={this.onChangeName}
										onBlur={this.onBlur}
										getErrorMessage={this.getErrorMessage}
										dataType={FieldDataType.SingleLineText}
									/>
								</FieldV5>

								<FieldV5 label="Portfolios">
									<ClickToEditInputV5
										value={this.portfolioGroupMemberCount}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>

								<FieldV5 label="Last Updated">
									<ClickToEditInputV5
										value={DateFormatter.format(portfolioGroup.lastModifiedAt)}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>

								<FieldV5 label="Last Updated By">
									<ClickToEditInputV5
										value={appState.actions.renderName(portfolioGroup.lastModifiedBy)}
										disabled={true}
										className="disabledInputField"
									/>
								</FieldV5>
							</div>
							<PortfolioGroupPortfoliosV5 portfolioGroup={portfolioGroup} />
						</UserGroupDetailsContentStyled>
					</>
				)}
			</NodataUserDetailsContentStyled>
		);
	}
}
